<template>
  <DefaultLayout>
    <BlogLayout
      :heading="heading"
      :breadcrumbItems="computedBreadcrumbItems"
      :categoryLinks="categoryLinks"
      :trendingData="trendingData"
      :listenData="listenData"
      :showTake5DailySidebar="true"
      :showListenElsewhere="showListenElsewhere"
      :breadcrumbHeadingTag="isListPage ? '' : 'div'"
    >
      <slot />
    </BlogLayout>
  </DefaultLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import DefaultLayout from '~/layouts/default.vue'
import BlogLayout from '~/layouts/blogLayout.vue'
import type { ContentItem, BreadcrumbItem, CategoryLink } from '~~/shared/types/common'
import podcastJson from '~/data/podcast.json'
import { URL_CONFIG } from '~~/shared/utils/url.const'
import { useTrending } from "~/composables/useSideBar";
import { CONTENT_TYPES } from '~~/shared/utils/contentTypes';

const route = useRoute()

withDefaults(
  defineProps<{
    heading?: string
    categoryLinks?: CategoryLink[]
    items?: ContentItem[]
    breadcrumbItems?: BreadcrumbItem[]
    isListPage?: boolean
  }>(),
  {
    heading: 'Take 5 Daily',
    categoryLinks: () => [],
    items: (): ContentItem[] => [],
    breadcrumbItems: () => [],
    isListPage: false
  }
);

const computedBreadcrumbItems = computed<BreadcrumbItem[]>(() => {
  const isChildPage =
    route.path.startsWith(URL_CONFIG.TAKE_FIVE + '/') &&
    route.path !== URL_CONFIG.TAKE_FIVE;
  return isChildPage
    ? [
        { label: 'Home', href: '/' },
        { label: 'Take 5 Daily', href: URL_CONFIG.TAKE_FIVE }
      ]
    : [{ label: "Home", href: "/" }, { label: "Take 5 Daily" }];
});

const categoryLinks = computed(() => {
  const normalize = (p: string) => {
    const parts = p.split('/').filter(Boolean);
    return '/' + parts.join('/');
  };
  const path = normalize(route.path);

  return [
  { label: 'Most Recent', href: URL_CONFIG.TAKE_FIVE },
  { label: 'Articles', href: URL_CONFIG.TAKE_FIVE_ARTICLES },
  { label: 'Videos', href: URL_CONFIG.TAKE_FIVE_VIDEOS },
  { label: 'Podcasts', href: URL_CONFIG.TAKE_FIVE_PODCASTS },
  ].map(link => {
    const href = normalize(link.href);
    const singular = href.endsWith('s') ? href.slice(0, -1) : null;

    return {
      ...link,
      active: path === href || (singular ? path.startsWith(singular + '/') : false)
    };
  });
});

const { data, error } = await useTrending();

const trendingData = computed(() => {
  if (!data.value) return []

  return data.value.map((item: any) => {
    const entityType = item.data?.entityType
    const urlKey = item.data?.urlKey

    let basePath = ''
    switch (entityType) {
      case CONTENT_TYPES.PODCASTS:
        basePath = URL_CONFIG.TAKE_FIVE_PODCAST
        break
      case CONTENT_TYPES.VIDEOS:
        basePath = URL_CONFIG.TAKE_FIVE_VIDEO
        break
      case CONTENT_TYPES.ARTICLES:
      default:
        basePath = URL_CONFIG.TAKE_FIVE_ARTICLE
        break
    }

    return {
      title: item.data?.name,
      href: `${basePath}/${urlKey}`,
      entityType
    }
  })
})

const listenData = computed(() => podcastJson)

const showListenElsewhere = computed(() => {
  const path = route.path.toLowerCase()
  return path.includes(`/${CONTENT_TYPES.PODCAST}`)
})

</script>
